import { bootstrapAnalytics } from "./analytics";
import { installHttpBridge } from "./bridge/HttpBridge";
import { pruneStoredTimelines } from "./diagnostics/TimelineStorage";
import { startStuckDetector } from "./diagnostics/StuckDetector";
import { startNetworkTelemetry } from "./network/NetworkTelemetry";

/**
 * Bootstrap the entire Upload Observability Platform.
 *
 * Composed of:
 *   1. Analytics (Phase 1) — provider swapped in-place after PostHog loads
 *   2. HTTP bridge (Phase 2) — axios interceptor stamps X-Correlation-Id
 *   3. Timeline pruning (Phase 2) — drops persisted timelines > 7d old
 *   4. Network telemetry (Phase 3) — window.online/offline + connection.change
 *   5. Stuck detector (Phase 4) — singleton sweep interval
 *   6. Uploader bridges (Phase 3) — attached lazily via announceUpload()
 *
 * Called once from App.js. Every subsystem's bootstrap is idempotent so
 * repeat calls are safe (StrictMode double-mount, HMR).
 */
let bootstrapPromise = null;

export const bootstrapObservability = () => {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = _bootstrap().catch((error) => {
    // eslint-disable-next-line no-console
    console.error("[observability] bootstrap failed", error);
  });
  return bootstrapPromise;
};

const _bootstrap = async () => {
  // Analytics first — early diagnostic events queue against the pending
  // provider and drain once PostHog (or Noop) is swapped in.
  await bootstrapAnalytics();
  installHttpBridge();
  startNetworkTelemetry();
  startStuckDetector();
  // Fire-and-forget housekeeping. Bounded on purpose so it can't cause an IDB
  // storm during page load.
  pruneStoredTimelines().catch(() => {});
};

export const __resetObservabilityBootstrapForTests = () => {
  bootstrapPromise = null;
};

export { getAnalytics, AnalyticsService } from "./analytics";
export { uploadDiagnosticsRegistry } from "./diagnostics/UploadDiagnosticsRegistry";
export { UPLOAD_EVENT, PAUSE_REASON, HEALTH } from "./diagnostics/UploadEvents";
export {
  computeHealth,
  buildHealthInput,
  DEFAULT_THRESHOLDS,
} from "./diagnostics/UploadHealthCalculator";
export {
  buildDiagnosticSnapshot,
  captureDiagnosticSnapshot,
} from "./diagnostics/DiagnosticSnapshot";
export {
  loadTimelineEntries,
  removeStoredTimeline,
} from "./diagnostics/TimelineStorage";
export {
  announceUpload,
  announceUploadTerminal,
  disposeUploadObservability,
} from "./uploadHook";
export { getNetworkSnapshot } from "./network/NetworkTelemetry";
